import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { terms } from '../language/terms';
import { userDirectoryInstance } from '../global-data/user-directory-instance';
import { personName } from './person-name';

/**
 * The plain-text name of a person known only by id, for text that cannot hold a <user-name>: a
 * toast, a confirmation message, a lock-conflict error (contact rule 8's exceptions).
 *
 * The server no longer sends a lock holder's name with the lock (D-162, #398 C12): a copied name
 * went stale when the person was renamed. So the name is read from the user directory when the
 * text is written, which is the person's CURRENT name (@CONTACT-012). An id the directory cannot
 * answer, or no id at all, reads as `fallback` ("another <user>" unless the caller names the
 * role): never the id itself.
 */
export async function personNameById(
  userId: string | null | undefined,
  fallback = `another ${terms.loginUser.lower}`
): Promise<string> {
  if (!userId) return fallback;
  const person = await userDirectoryInstance().getUserDto(userId);
  return person ? personName(person.displayName) : fallback;
}

/**
 * Keeps the plain-text name of one person a page follows by id, such as the holder of a record's
 * lock, for a message built at render (the Take Over Review confirmation). The page calls
 * `follow(userId)` whenever the id it holds may have changed. The person is looked up in the
 * directory, and watched there while the page is attached, so a rename the server announces while
 * the page is open is the name the next confirmation shows (@CONTACT-012). `name` is null until the
 * lookup lands, and for no id, so the page writes its own fallback. An answer that lands after the
 * page has moved to a different person is dropped, so a slow answer never names the wrong one.
 */
export class PersonNameController implements ReactiveController {
  name: string | null = null;
  private followedId: string | null = null;
  private unwatch?: () => void;

  constructor(
    private readonly host: ReactiveControllerHost,
    private readonly fallback?: string
  ) {
    host.addController(this);
  }

  follow(userId: string | null | undefined): void {
    const id = userId || null;
    if (id === this.followedId) return;
    this.followedId = id;
    this.name = null;
    this.watch();
  }

  /** A page that comes back looks the person up again rather than trusting the name it left with. */
  hostConnected(): void {
    this.watch();
  }

  /** A page that leaves stops listening for the person, so nothing is kept alive by it. */
  hostDisconnected(): void {
    this.unwatch?.();
    this.unwatch = undefined;
  }

  private watch(): void {
    this.hostDisconnected();
    const id = this.followedId;
    if (!id) return;
    const named = (name: string) => {
      if (this.followedId !== id) return;
      this.name = name;
      this.host.requestUpdate();
    };
    this.unwatch = userDirectoryInstance().subscribe(id, (person) =>
      named(personName(person.displayName))
    );
    void personNameById(id, this.fallback).then(named);
  }
}
